import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, User, Link2, Newspaper, AlertCircle, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";

export default function NewsDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/news/${id}`)
      .then(({ data }) => setNews(data))
      .catch(() => setError("News non trovata"))
      .finally(() => setLoading(false));
  }, [id]);

  const copyLink = async () => {
    const url = `${window.location.origin}/news/${id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Link Copiato!", { duration: 2500 });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[var(--lssd-olive-deep)] text-[var(--lssd-khaki)]" data-testid="news-detail-loading">
        Caricamento…
      </div>
    );
  }
  if (error || !news) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 bg-[var(--lssd-olive-deep)]" data-testid="news-detail-error">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-[var(--lssd-khaki)] mx-auto mb-4" />
          <h2 className="font-display font-black text-2xl text-[var(--lssd-cream)] mb-2">News non trovata</h2>
          <p className="text-[var(--lssd-cream)]/60 mb-6">Il comunicato richiesto non esiste o è stato rimosso.</p>
          <Link to="/">
            <Button className="bg-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki-light)] text-[var(--lssd-olive-deep)] rounded-full font-semibold uppercase tracking-[0.16em]">
              Torna alla Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--lssd-olive-deep)] min-h-screen">
      <article className="max-w-4xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="news-detail-page">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-2 text-[var(--lssd-khaki)] hover:text-[var(--lssd-cream)] text-[11px] uppercase tracking-[0.22em] font-semibold mb-8 transition-colors"
          data-testid="news-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Indietro
        </button>

        <div className="inline-flex items-center gap-2 mb-4">
          <Newspaper className="h-4 w-4 text-[var(--lssd-khaki)]" />
          <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[var(--lssd-khaki)]">
            Comunicato Ufficiale
          </span>
        </div>

        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[var(--lssd-cream)] leading-tight mb-6" data-testid="news-detail-title">
          {news.title}
        </h1>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-xs text-[var(--lssd-cream)]/60 mb-8 pb-8 border-b border-[var(--lssd-khaki)]/20">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(news.created_at).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" />
            {news.author_role && <span className="text-[var(--lssd-khaki)] font-bold">{news.author_role}</span>}
            {news.author_role && <span className="text-[var(--lssd-khaki)]/40">·</span>}
            {news.author}
          </span>
          <button
            onClick={copyLink}
            data-testid="news-copy-link-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 ml-auto bg-[var(--lssd-khaki)]/15 hover:bg-[var(--lssd-khaki)]/25 border border-[var(--lssd-khaki)]/40 text-[var(--lssd-khaki)] rounded-full uppercase text-[10px] tracking-[0.2em] font-semibold transition-colors"
          >
            <Link2 className="h-3.5 w-3.5" /> Copia Link
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/news/${news.id}/bulletin.jpg`}
            data-testid="news-bulletin-jpg-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki-light)] text-[var(--lssd-olive-deep)] rounded-full uppercase text-[10px] tracking-[0.2em] font-semibold transition-colors"
          >
            <FileDown className="h-3.5 w-3.5" /> Bollettino PR (JPG)
          </a>
        </div>

        {news.image_path && (
          <div className="mb-10 border border-[var(--lssd-khaki)]/25 rounded-2xl overflow-hidden bg-[var(--lssd-olive-dark)]" data-testid="news-detail-image">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/news/image/${news.image_path}`}
              alt={news.title}
              className="w-full h-auto"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        )}

        <div className="text-[var(--lssd-cream)]/85 text-lg leading-relaxed whitespace-pre-wrap" data-testid="news-detail-content">
          {news.content}
        </div>

        <div className="mt-16 pt-8 border-t border-[var(--lssd-khaki)]/20 flex items-center justify-between flex-wrap gap-4">
          <Link
            to="/"
            className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--lssd-khaki)] hover:text-[var(--lssd-cream)] transition-colors"
          >
            ← Tutte le News
          </Link>
          <button
            onClick={copyLink}
            data-testid="news-copy-link-btn-bottom"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--lssd-khaki)]/15 hover:bg-[var(--lssd-khaki)]/25 border border-[var(--lssd-khaki)]/40 text-[var(--lssd-khaki)] rounded-full uppercase text-[11px] tracking-[0.2em] font-semibold transition-colors"
          >
            <Link2 className="h-4 w-4" /> Condividi questo Comunicato
          </button>
        </div>
      </article>
    </div>
  );
}
